import { EDIT_CLIENT_PROFILE_SOURCES } from 'src/constants/analytics';
import CoreAnalytics from './coreAnalytics';

/** @typedef {import('./analyticsTypes').ClientProfileEventPayload} */
/** @typedef {import('./analyticsTypes').ClientProfileSavedEventPayload} */
/** @typedef {import('./analyticsTypes').ClientProfileTabSwitchedEventPayload} */

/**
 * Analytics service related to the client-profile edit modal.
 */
class EditClientProfileAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    PROFILE_OPENED: 'client_profile_opened',
    PROFILE_SAVED: 'client_profile_saved',
    PROFILE_CLOSED_WITHOUT_SAVING: 'client_profile_closed_without_saving',
    PROFILE_TAB_SWITCHED: 'client_profile_tab_switched',
    PROFILE_SCHEDULING_GROUPS_EXPANDED: 'client_profile_scheduling_groups_expanded',
  };

  TRACKED_SOURCES = [EDIT_CLIENT_PROFILE_SOURCES.SCHEDULING];

  shouldTrackEvent({ targetClientId, source }) {
    return Boolean(targetClientId) && this.TRACKED_SOURCES.includes(source);
  }

  /**
   * Track when the client-profile edit modal is opened.
   *
   * @param {ClientProfileEventPayload} payload
   */
  trackProfileOpened({ targetClientId, source }) {
    if (!this.shouldTrackEvent({ targetClientId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_OPENED, { targetClientId, source, eventVersion: 1 });
  } // trackProfileOpened()

  /**
   * Track when the client-profile edit modal is saved.
   *
   * @param {ClientProfileSavedEventPayload} payload
   */
  trackProfileSaved({ targetClientId, source, hasChanges }) {
    if (!this.shouldTrackEvent({ targetClientId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_SAVED, {
      targetClientId, source, hasChanges, eventVersion: 1,
    });
  } // trackProfileSaved()

  /**
   * Track when the client-profile edit modal is closed without saving.
   *
   * @param {ClientProfileEventPayload} payload
   */
  trackProfileClosedWithoutSaving({ targetClientId, source }) {
    if (!this.shouldTrackEvent({ targetClientId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_CLOSED_WITHOUT_SAVING, { targetClientId, source, eventVersion: 1 });
  } // trackProfileClosedWithoutSaving()

  /**
   * Track when the user switches tabs within the client-profile edit modal.
   *
   * @param {ClientProfileTabSwitchedEventPayload} payload
   */
  trackProfileTabSwitched({
    targetClientId, source, fromTab, toTab,
  }) {
    if (!this.shouldTrackEvent({ targetClientId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_TAB_SWITCHED, {
      targetClientId, source, fromTab, toTab, eventVersion: 1,
    });
  } // trackProfileTabSwitched()

  /**
   * Track when the "Scheduling Groups" default data-field section on the Information tab
   * is expanded.
   *
   * @param {ClientProfileEventPayload} payload
   */
  trackProfileSchedulingGroupsExpanded({ targetClientId, source }) {
    if (!this.shouldTrackEvent({ targetClientId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_SCHEDULING_GROUPS_EXPANDED, {
      targetClientId, source, eventVersion: 1,
    });
  } // trackProfileSchedulingGroupsExpanded()
}

export default new EditClientProfileAnalytics();
